import React, { useEffect, useRef, useState } from "react";
import { UnoStoreHooks, defaultLogo } from "@simulanisindia/ui";
import { useParams } from "react-router-dom";
import ResourceSection from "./ResourceSection";
import {
  APPLICATION_LIMIT,
  MODULE_LIMIT,
  RESOURCE_APPLICATION_TAB,
  RESOURCE_ENVIRONMENT_TAB,
  RESOURCE_MODULE_TAB,
  RESOURCE_SKILL_SCHOOL_TAB,
  RESOURCE_SKILL_STREAM_TAB,
  AR_CONTENT_TAB,
  constantKeyMap
} from "../../constant/constants";
import { getAllocatedModuleList, getAssignedApplication } from "../../apiClient/apiDataFetcher";
import { makeFilterParams, visibleAssignSectionList } from "../../utils/general";
import { useSelector } from "react-redux";
import ResourceFilter from "./ResourceFilter";
import useDebounce from "../../hooks/useDebounce";

const { useUnoSelector } = UnoStoreHooks;

const ResourceListPanel = ({
  selectedResource,
  setSelectedResource,
  activeTab,
  checkBoxAll,
  setCheckBoxAll,
  selectCheckBoxAllClicked,
  isMobileView,
  checkedFilterItem,
  setCheckedFilterItem
}) => {
  const params = useParams();
  const { uuid } = params;
  const [showfilter, setShowFilter] = useState(false);
  const userData = useUnoSelector((state) => state.user);
  const { searchValue } = useSelector(state => state.searchSlice)
  const configData = useUnoSelector((state) => state.config);
  const UNO_URL = configData?.config?.UNO_URL;

  const [search, setSearch] = useState({
    [RESOURCE_APPLICATION_TAB]: "",
    [RESOURCE_MODULE_TAB]: "",
    [RESOURCE_ENVIRONMENT_TAB]: "",
    [RESOURCE_SKILL_STREAM_TAB]: "",
    [RESOURCE_SKILL_SCHOOL_TAB]: "",
    [AR_CONTENT_TAB]: "",
  });
  const [loading, setLoading] = useState({
    [RESOURCE_APPLICATION_TAB]: true,
    [RESOURCE_MODULE_TAB]: true,
    [RESOURCE_ENVIRONMENT_TAB]: true,
    [RESOURCE_SKILL_STREAM_TAB]: true,
    [RESOURCE_SKILL_SCHOOL_TAB]: true,
    [AR_CONTENT_TAB]: true,
  });
  // const [checkedFilterItem, setCheckedFilterItem] = useState({
  //   [RESOURCE_APPLICATION_TAB]: [],
  //   [RESOURCE_MODULE_TAB]: [],
  //   [RESOURCE_ENVIRONMENT_TAB]: [],
  //   [RESOURCE_SKILL_STREAM_TAB]: [],
  //   [RESOURCE_SKILL_SCHOOL_TAB]: [],
  // });

  let querySearch = useDebounce(search, 500);
  let queryCheckedItem = useDebounce(checkedFilterItem, 500);

  const prevQuerySearch = useRef(querySearch);
  const prevQueryCheckedItem = useRef(queryCheckedItem);

  const [applicationList, setApplicationList] = useState({
    hasMore: true,
    page: 1,
    list: [],
  });
  const [moduleList, setModuleList] = useState({
    hasMore: true,
    page: 1,
    list: [],
  });
  const [environmentList, setEnvironmentList] = useState({
    hasMore: true,
    page: 1,
    list: [],
  });
  const [skillStreamList, setSkillStreamList] = useState({
    hasMore: true,
    page: 1,
    list: [],
  });

  const [skillSchoolList, setSkillSchoolList] = useState({
    hasMore: true,
    page: 1,
    list: [],
  });
  const [arContentList, setArContentList] = useState({
    hasMore: true,
    page: 1,
    list: [],
  });

  const getPaginatedAssignedModules = async (pageNumber, querySearch, selectedItem = [], resourceType) => {
    try {
      let filterParams = {};
      if (selectedItem.length > 0) {
        filterParams = makeFilterParams(selectedItem);
      }
      let page = pageNumber || 1;
      let queryParams = {
        page: page,
        search: querySearch,
        ...filterParams,
      };

      let data = await getAllocatedModuleList({ UNO_URL, UUID: userData.user.uuid, queryParams, resourceType });

      let list = data.list;

      let fetchStates = {
        [RESOURCE_MODULE_TAB]: setModuleList,
        [RESOURCE_ENVIRONMENT_TAB]: setEnvironmentList,
        [RESOURCE_SKILL_STREAM_TAB]: setSkillStreamList,
        [RESOURCE_SKILL_SCHOOL_TAB]: setSkillSchoolList,
        [AR_CONTENT_TAB]: setArContentList,
      };

      fetchStates[resourceType]((pre) => ({
        hasMore: list.length === MODULE_LIMIT,
        page: list.length === MODULE_LIMIT ? page + 1 : page,
        list: page === 1 ? [...list] : [...pre.list, ...list],
      }));
    } catch (error) {
      console.log(error);
    } finally {
      setLoading((pre) => ({
        ...pre,
        [resourceType]: false,
      }));
    }
  };

  const getPaginatedApplications = async (pageNumber, querySearch, selectedItem = []) => {
    try {
      let page = pageNumber || 1;
      let queryParams = {
        page: page,
        search: querySearch,
        tagIds: selectedItem.length > 0 ? selectedItem.map((e) => e.key) : null,
      };
      let data = await getAssignedApplication({ UNO_URL, UUID: userData.user.uuid, queryParams });

      setApplicationList((pre) => ({
        hasMore: data.length === APPLICATION_LIMIT,
        page: data.length === APPLICATION_LIMIT ? page + 1 : page,
        list: page === 1 ? [...data] : [...pre.list, ...data],
      }));
    } catch (error) {
      console.log(error);
    } finally {
      setLoading((pre) => ({
        ...pre,
        [RESOURCE_APPLICATION_TAB]: false,
      }));
    }
  };

  useEffect(() => {
    if (queryCheckedItem) {
      Object.keys(querySearch).forEach((tab) => {
        if (prevQuerySearch.current[tab] !== querySearch[tab] || prevQueryCheckedItem.current[tab] !== queryCheckedItem[tab]) {
          if (tab === RESOURCE_APPLICATION_TAB) {
            getPaginatedApplications(1, querySearch[tab], queryCheckedItem[tab], tab);
          } else {
            getPaginatedAssignedModules(1, querySearch[tab], queryCheckedItem[tab], tab);
          }
        }
      });
      prevQuerySearch.current = querySearch;
      prevQueryCheckedItem.current = queryCheckedItem;
    }
  }, [querySearch, queryCheckedItem]);

  const openFilter = (e) => {
    setShowFilter((pre) => !pre);
  };

  const loadMoreData = () => {
    const mapping = {
      [RESOURCE_APPLICATION_TAB]: {
        storedValue: applicationList,
        callerFunction: getPaginatedApplications,
      },
      [RESOURCE_MODULE_TAB]: {
        storedValue: moduleList,
        callerFunction: getPaginatedAssignedModules,
      },
      [RESOURCE_ENVIRONMENT_TAB]: {
        storedValue: environmentList,
        callerFunction: getPaginatedAssignedModules,
      },
      [RESOURCE_SKILL_STREAM_TAB]: {
        storedValue: skillStreamList,
        callerFunction: getPaginatedAssignedModules,
      },
      [RESOURCE_SKILL_SCHOOL_TAB]: {
        storedValue: skillSchoolList,
        callerFunction: getPaginatedAssignedModules,
      },
      [AR_CONTENT_TAB]: {
        storedValue: arContentList,
        callerFunction: getPaginatedAssignedModules,
      },
    };
    mapping[activeTab].callerFunction(
      mapping[activeTab].storedValue.page,
      search[activeTab],
      queryCheckedItem[activeTab],
      activeTab
    );
  };

  const fetchInitialAppData = () => {
    setLoading({
      [RESOURCE_APPLICATION_TAB]: true,
      [RESOURCE_MODULE_TAB]: true,
      [RESOURCE_ENVIRONMENT_TAB]: true,
      [RESOURCE_SKILL_STREAM_TAB]: true,
      [RESOURCE_SKILL_SCHOOL_TAB]: true,
      [AR_CONTENT_TAB]: true,
    });
    let _visibleSectionList = visibleAssignSectionList().map(e => e.id_link);
    if (!(_visibleSectionList.length)) return;
    let allowSectionList = _visibleSectionList.map(e => constantKeyMap[e]);
    if (allowSectionList.includes(RESOURCE_APPLICATION_TAB)) getPaginatedApplications(1, null, []);
    if (allowSectionList.includes(RESOURCE_MODULE_TAB)) getPaginatedAssignedModules(1, null, [], RESOURCE_MODULE_TAB);
    if (allowSectionList.includes(RESOURCE_ENVIRONMENT_TAB)) getPaginatedAssignedModules(1, null, [], RESOURCE_ENVIRONMENT_TAB);
    if (allowSectionList.includes(RESOURCE_SKILL_STREAM_TAB)) getPaginatedAssignedModules(1, null, [], RESOURCE_SKILL_STREAM_TAB);
    if (allowSectionList.includes(RESOURCE_SKILL_SCHOOL_TAB)) getPaginatedAssignedModules(1, null, [], RESOURCE_SKILL_SCHOOL_TAB);
    if (allowSectionList.includes(AR_CONTENT_TAB)) getPaginatedAssignedModules(1, null, [], AR_CONTENT_TAB);
  };

  useEffect(() => {
    fetchInitialAppData();
  }, [uuid]);

  const handleSetSearch = (e) => {
    setSearch((pre) => ({ ...pre, [activeTab]: e.target.value }));
  };

  useEffect(() => {
    if (activeTab) {
      setSearch((pre) => ({ ...pre, [activeTab]: searchValue }));
    }
  }, [searchValue]);

  let renderList = {
    [RESOURCE_APPLICATION_TAB]:
      applicationList.list
        .filter((e) => e.uuid !== configData.config.applicationId)
        .map((item) => ({ title: item.name, id: item.uuid, icon: item.icon })) || [],
    [RESOURCE_MODULE_TAB]:
      moduleList.list.map((item) => ({
        title: item.name,
        id: item.uuid,
        description: item?.versions[0]?.data?.short || null,
        icon: item?.versions[0]?.data?.thumbnail || defaultLogo,
      })) || [],
    [RESOURCE_ENVIRONMENT_TAB]:
      environmentList.list.map((item) => ({
        title: item.name,
        id: item.uuid,
        description: item?.versions[0]?.data?.description || null,
        icon: item?.versions[0]?.data?.thumbnail || defaultLogo,
      })) || [],
    [RESOURCE_SKILL_STREAM_TAB]:
      skillStreamList.list.map((item) => ({
        title: item.name,
        id: item.uuid,
        description: item.description,
        icon: item?.versions[0]?.data?.thumbnail || defaultLogo,
      })) || [],
    [RESOURCE_SKILL_SCHOOL_TAB]:
      skillSchoolList.list.map((item) => ({
        title: item.name,
        id: item.uuid,
        description: item.description,
        icon: item?.versions[0]?.data?.thumbnail || defaultLogo,
      })) || [],
    [AR_CONTENT_TAB]:
      arContentList.list.map((item) => ({
        title: item.name,
        id: item.uuid,
        description: item?.versions[0]?.data?.description || null,
        icon: item?.versions[0]?.data?.thumbnail || defaultLogo,
      })) || [],
  };

  let haveSomeMoreData = {
    [RESOURCE_APPLICATION_TAB]: applicationList.hasMore,
    [RESOURCE_MODULE_TAB]: moduleList.hasMore,
    [RESOURCE_ENVIRONMENT_TAB]: environmentList.hasMore,
    [RESOURCE_SKILL_STREAM_TAB]: skillStreamList.hasMore,
    [RESOURCE_SKILL_SCHOOL_TAB]: skillSchoolList.hasMore,
    [AR_CONTENT_TAB]: arContentList.hasMore,
  };

  return (
    <>
      <ResourceSection
        renderList={renderList[activeTab]}
        allTabResources={renderList}
        hasMore={haveSomeMoreData[activeTab]}
        loading={loading[activeTab]}
        search={search[activeTab]}
        setSearch={handleSetSearch}
        selectedResource={selectedResource}
        setSelectedResource={setSelectedResource}
        openFilter={openFilter}
        activeTab={activeTab}
        checkedFilterItem={checkedFilterItem}
        loadMoreData={loadMoreData}
        checkBoxAll={checkBoxAll}
        setCheckBoxAll={setCheckBoxAll}
        selectCheckBoxAllClicked={selectCheckBoxAllClicked}
        isMobileView={isMobileView}
      />

      {!isMobileView && activeTab &&
        <ResourceFilter
          showfilter={showfilter}
          openFilter={openFilter}
          activeTab={activeTab}
          checkedFilterItem={checkedFilterItem}
          setCheckedFilterItem={setCheckedFilterItem}
        />}
    </>
  );
};

export default ResourceListPanel;
